import React, { useRef, useEffect } from "react";

interface LightningProps {
  lightningColor?: [number, number, number]; // RGB 0–1
  xOffset?: number;
  speed?: number;
  intensity?: number;
  size?: number;
  background?: [number, number, number]; // RGB 0–1
  style?: React.CSSProperties;
}

const Lightning: React.FC<LightningProps> = ({
  lightningColor = [0, 0, 0], // black by default
  xOffset = 0,
  speed = 1,
  intensity = 1,
  size = 1,
  background = [1, 1, 1],
  style,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const uniformLocationsRef = useRef<any>({});
  const backgroundRef = useRef(background);
  const lightningColorRef = useRef(lightningColor);

  useEffect(() => {
    backgroundRef.current = background;
    lightningColorRef.current = lightningColor;
  }, [background, lightningColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      glRef.current?.viewport(0, 0, canvas.width, canvas.height);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const gl = canvas.getContext("webgl", { preserveDrawingBuffer: true });
    if (!gl) return;
    glRef.current = gl;

    // ---------- Shaders ----------
    const vertexShaderSource = `
      attribute vec2 aPosition;
      void main() {
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision mediump float;

      uniform vec2 iResolution;
      uniform float iTime;
      uniform float uXOffset;
      uniform float uSpeed;
      uniform float uIntensity;
      uniform float uSize;
      uniform vec3 uBackground;
      uniform vec3 uLightningColor;

      #define OCTAVE_COUNT 10

      float hash12(vec2 p) {
        vec3 p3 = fract(vec3(p.xyx) * .1031);
        p3 += dot(p3, p3.yzx + 33.33);
        return fract((p3.x + p3.y) * p3.z);
      }

      mat2 rotate2d(float a) {
        float c = cos(a), s = sin(a);
        return mat2(c, -s, s, c);
      }

      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        float a = hash12(i);
        float b = hash12(i + vec2(1.0, 0.0));
        float c = hash12(i + vec2(0.0, 1.0));
        float d = hash12(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }

      float fbm(vec2 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < OCTAVE_COUNT; i++) {
          v += a * noise(p);
          p = rotate2d(0.45) * p * 2.0;
          a *= 0.5;
        }
        return v;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / iResolution.xy;
        uv = uv * 2.0 - 1.0;
        uv.x *= iResolution.x / iResolution.y;
        uv.x += uXOffset;

        uv += 2.0 * fbm(uv * uSize + iTime * uSpeed * 0.8) - 1.0;

        float dist = abs(uv.y) + 0.01;

        vec3 lightning = uLightningColor * pow(0.07 / dist, 1.0) * uIntensity;
        float strength = pow(0.07 / dist, 2.0) * uIntensity;

        vec3 color = uBackground + (uLightningColor * 2.0 - 1.0) * strength;

        color = clamp(color, 0.0, 1.0);
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const compile = (src: string, type: number) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(s)!);
      }
      return s;
    };

    const program = gl.createProgram()!;
    gl.attachShader(program, compile(vertexShaderSource, gl.VERTEX_SHADER));
    gl.attachShader(program, compile(fragmentShaderSource, gl.FRAGMENT_SHADER));
    gl.linkProgram(program);
    gl.useProgram(program);
    programRef.current = program;

    uniformLocationsRef.current = {
      iResolution: gl.getUniformLocation(program, "iResolution"),
      iTime: gl.getUniformLocation(program, "iTime"),
      uXOffset: gl.getUniformLocation(program, "uXOffset"),
      uSpeed: gl.getUniformLocation(program, "uSpeed"),
      uIntensity: gl.getUniformLocation(program, "uIntensity"),
      uSize: gl.getUniformLocation(program, "uSize"),
      uBackground: gl.getUniformLocation(program, "uBackground"),
      uLightningColor: gl.getUniformLocation(program, "uLightningColor"),
    };

    const buffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const pos = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const start = performance.now();

    const render = () => {
      resizeCanvas();
      gl.clear(gl.COLOR_BUFFER_BIT);

      const t = (performance.now() - start) / 1000;

      gl.uniform2f(uniformLocationsRef.current.iResolution, canvas.width, canvas.height);
      gl.uniform1f(uniformLocationsRef.current.iTime, t);
      gl.uniform1f(uniformLocationsRef.current.uXOffset, xOffset);
      gl.uniform1f(uniformLocationsRef.current.uSpeed, speed);
      gl.uniform1f(uniformLocationsRef.current.uIntensity, intensity);
      gl.uniform1f(uniformLocationsRef.current.uSize, size);
      gl.uniform3fv(uniformLocationsRef.current.uBackground, backgroundRef.current);
      gl.uniform3fv(uniformLocationsRef.current.uLightningColor, lightningColorRef.current);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      requestAnimationFrame(render);
    };

    render();
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  return <canvas ref={canvasRef} className="w-full h-full" style={style} />;
};

export default Lightning;
